import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { AlertTriangle } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { IS_APP_BUILD } from '../lib/buildTarget';
import { fuenteDePro } from '../lib/fuenteDePro';
import { GiroEncima, APRETADO, IconoDeVentana, ListaConVinetas } from './alumno';
import { FONDO_DE_LA_VENTANA, BOTON_VENTANA_SECUNDARIO, BOTON_VENTANA_BORRAR } from './panel/VentanaDelPanel';
import { useHojaDeLaCasa, FilaDeAgarradera, ENTRA_EL_VELO, ENTRA_LA_CAJA } from './alumno/arrastreDeHoja';

// UN solo modal de borrado de cuenta, dos puertas (Settings y, desde t7-1 #466,
// el ultimo renglon del invitado en el menu lateral; antes era el banner guest
// del Dashboard). Registrados: advertencias completas + codigo por correo.
// Guests: advertencia adaptada (se borra AHORA) y sin codigo.
//
// pantallas-alumno (G), AJ10 y AJ11 de la parte 2: LA VENTANA DEL PANEL, con su contenido de hoy.
//   . el velo de la casa (fijo en los cuatro bordes, negro al 66 %, z 70) en un portal a `body`: ya cubre TODA la
//     pantalla, tambien la barra de arriba (hoy empezaba a media barra);
//   . la caja de la ventana H (440; en el celular hoja desde abajo) con el borde rojo oscuro y el triangulo en el
//     cuadro rojo suave. SIN X, como hoy, y el velo no cierra (como hoy);
//   . las advertencias en vinetas DIBUJADAS; la palabra que hoy va en mayusculas ("TODAY", "NOT", "RIGHT NOW") en
//     negritas. Los textos son los de hoy: la negrita se pone al pintar, sin tocar los locales;
//   . botones de la ventana: en el celular a lo ancho y el rojo ARRIBA; trabajando con giro, del mismo ancho, y
//     "Keep my account" apagado mientras trabaja.

/* La palabra en mayusculas de una advertencia (dos o mas letras, y las que la siguen en mayusculas), en negritas. */
export function conMayusculasEnNegritas(texto) {
  const partes = String(texto || '').split(/(\b[A-ZÁÉÍÓÚÑ]{2,}(?:\s+[A-ZÁÉÍÓÚÑ]{2,})*\b)/);
  return partes.map((p, i) => (i % 2 === 1
    ? <strong key={i} className="font-semibold text-[#F4F4F3]">{p}</strong>
    : <React.Fragment key={i}>{p}</React.Fragment>));
}

export default function DeleteAccountModal({ open, onClose }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  // post-t7-2b remate (R2, 5.2): gana `common` por el aviso del codigo que no salio.
  const { t } = useTranslation(['settings', 'common']);
  const [step, setStep] = useState('warn');   // warn | guest_confirm | code
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const isGuest = !!user?.is_guest;

  const close = () => { setStep('warn'); setCode(''); onClose(); };
  // marca-1 (#726): la hoja entra y sale como las demas y cierra al bajar la agarradera (no a media peticion,
  // como "Conservar").
  const hoja = useHojaDeLaCasa(close, { abierta: !!open, cerrable: !busy });

  if (!open) return null;

  const start = async () => {
    setBusy(true);
    try {
      const res = await api.post('/auth/delete-account/request');
      if (res.data.code_required) {
        setStep('code');
        // post-t7-2b remate (R2, 5.2): si el correo no salio (y no vino el codigo), se dice
        // eso en vez de "Te enviamos un codigo". Sin `email_sent` (servidor viejo), como antes.
        if (res.data.email_sent === false && !res.data.verification_code) {
          toast.error(t('common:prod.codigo.nosalio'));
        } else {
          toast.success(t('delete_account.code_sent'));
        }
      } else {
        setStep('guest_confirm');
      }
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error((typeof d === 'string' && d) || t('delete_account.failed'));
    } finally { setBusy(false); }
  };

  const confirm = async () => {
    setBusy(true);
    try {
      await api.post('/auth/delete-account/confirm',
        step === 'code' ? { code: code.trim() } : {});
      toast.success(t('delete_account.done'));
      // force: la cuenta ya fue borrada server-side — los renglones locales
      // pendientes no tienen a dónde subir; la guardia D-D aquí solo
      // ofrecería una elección imposible.
      // t7-1c (Bloque U): en la WEB se navega a la portada con `replace` EN EL
      // MISMO TICK que `logout()`, el camino que t7-1b (#477) le dio a cerrar
      // sesion en los dos menus. En la APP la secuencia es la de antes: logout
      // completo y DESPUES "/".
      // ajustes-1-3-0 (Bloque H2, #507): `cuentaBorrada` purga la bodega local aunque el
      // cierre de sesion normal ya la conserve: la cuenta ya no existe en el servidor.
      const cierre = logout({ force: true, cuentaBorrada: true });
      if (!IS_APP_BUILD) navigate('/', { replace: true });
      await cierre;
      if (IS_APP_BUILD) navigate('/');
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error((typeof d === 'string' && d) || t('delete_account.failed'));
    } finally { setBusy(false); }
  };

  // TANDA AS-2: con una suscripción cobrada por APPLE, la advertencia de arriba
  // ("se cancela hoy") sería MENTIRA — nadie puede cancelar una suscripción de
  // Apple desde el servidor. Se sustituye por la advertencia que sí es cierta y
  // que le dice a la persona qué hacer en su iPhone ANTES de borrar la cuenta.
  // REMATES-1-2-1 (remate, #458): manda la fuente VIVA (`billing_source`), con la
  // misma caída a `billing_provider` que Ajustes si el campo no viene.
  const isAppleBilled = fuenteDePro(user) === 'apple';
  const warnings = isGuest
    ? [t('delete_account.warn_guest_now')]
    : [isAppleBilled ? t('delete_account.warn_apple_subscription') : t('delete_account.warn_subscription'),
       t('delete_account.warn_data'), t('delete_account.warn_block')];

  const botonRojo = (onClick, disabled, testid, texto) => (
    <button type="button" onClick={onClick} disabled={disabled || busy} aria-busy={busy || undefined}
      data-trabajando={busy ? 'true' : undefined}
      className={`relative ${BOTON_VENTANA_BORRAR} ${APRETADO}`} data-testid={testid}>
      <GiroEncima trabajando={busy}>{texto}</GiroEncima>
    </button>
  );
  const botonConservar = (testid) => (
    <button type="button" onClick={close} disabled={busy}
      className={`${BOTON_VENTANA_SECUNDARIO} ${APRETADO}`} data-testid={testid}>
      {t('delete_account.keep')}
    </button>
  );
  const PIE = 'shrink-0 px-6 pt-4 pb-[max(1.5rem,env(safe-area-inset-bottom))] md:pb-6 flex flex-col-reverse md:flex-row md:justify-end gap-2';

  return createPortal(
    <div ref={hoja.veloRef} className={`${FONDO_DE_LA_VENTANA} ${ENTRA_EL_VELO}`} data-testid="delete-account-modal">
      <div ref={hoja.cajaRef} role="dialog" aria-modal="true" aria-labelledby="delete-account-title"
        className={`w-full md:max-w-[440px] max-h-[90dvh] flex flex-col bg-[#161616] border border-[#4A1F1F] rounded-t-[24px] md:rounded-[20px] shadow-2xl ${ENTRA_LA_CAJA}`}
        data-testid="delete-account-panel">
        <FilaDeAgarradera agarradera={hoja.agarradera} testid="delete-account-agarradera" />
        <div className="flex-1 min-h-0 overflow-y-auto px-6 pt-4 md:pt-6 flex flex-col gap-4 max-md:overscroll-contain max-md:touch-pan-y">
          <IconoDeVentana tono="rojo" icono={AlertTriangle} testid="delete-account-icono" />
          <h3 id="delete-account-title" className="text-[17px] font-semibold text-[#F4F4F3] break-words">
            {t('delete_account.modal_title')}
          </h3>
          {step !== 'code' ? (
            <ListaConVinetas testid="delete-account-warnings" renglones={warnings.map((w) => conMayusculasEnNegritas(w))} />
          ) : (
            <>
              <p className="text-[14px] leading-5 text-[#C8C8C8]">{t('delete_account.code_prompt')}</p>
              <input value={code} onChange={(e) => setCode(e.target.value)}
                placeholder="000000" maxLength={6} inputMode="numeric" autoComplete="one-time-code"
                aria-label={t('delete_account.code_prompt')}
                className="w-full h-14 px-3.5 rounded-xl bg-[#0E0E0E] border border-[#262626] text-center tracking-[8px] text-[22px] font-semibold text-[#F4F4F3] placeholder-[#6A6A6A] outline-none focus:border-[#F27A2E]"
                data-testid="delete-code-input" />
            </>
          )}
        </div>
        {step !== 'code' ? (
          <div className={PIE}>
            {botonConservar('delete-keep')}
            {step === 'guest_confirm'
              ? botonRojo(confirm, false, 'delete-confirm', t('delete_account.confirm_final'))
              : botonRojo(start, false, 'delete-continue', t('delete_account.continue'))}
          </div>
        ) : (
          <div className={PIE}>
            {botonConservar('delete-keep')}
            {botonRojo(confirm, code.trim().length !== 6, 'delete-confirm', t('delete_account.confirm_final'))}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
